import React, { useEffect, useMemo, useState } from "react";

import api from "../../api/api";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  AssignmentOutlined,
  AutoStoriesOutlined,
  CalendarMonthOutlined,
  CheckCircleOutline,
  ClassOutlined,
  DescriptionOutlined,
  EventNoteOutlined,
  MenuBookOutlined,
  SchoolOutlined,
  ScienceOutlined,
  TimerOutlined,
  TopicOutlined,
} from "@mui/icons-material";

import Swal from "sweetalert2";

const COLORS = {
  black: "#000000",
  white: "#FFFFFF",
  deepYellow: "#D4A017",
  orange: "#F97316",
  camel: "#C19A6B",
  lightCamel: "#F5EBDD",
  lightGray: "#F6F7F9",
  border: "#E2E5E9",
  text: "#202124",
  muted: "#6B7280",
  softBlack: "#181818",
  success: "#16803C",
  danger: "#C62828",
};

const MATERIAL_OPTIONS = [
  { value: "notebook", label: "Notebook" },
  { value: "chalk", label: "Chalk" },
  { value: "marker", label: "Marker" },
  { value: "lesson_book", label: "Lesson Book" },
  { value: "pen", label: "Pen" },
  { value: "duster", label: "Duster" },
  { value: "handout", label: "Handout" },
  { value: "pamphlet_booklet", label: "Pamphlet / Booklet" },
  { value: "poster", label: "Poster" },
  { value: "textbook", label: "Textbook" },
  { value: "workbook", label: "Workbook" },
  { value: "flash_cards", label: "Flash Cards" },
  { value: "chart", label: "Chart" },
  { value: "whiteboard", label: "Whiteboard" },
  { value: "projector", label: "Projector" },
  { value: "computer", label: "Computer" },
  { value: "other", label: "Other" },
];

const initialFormData = {
  subject: "",
  grade_class: "",
  period: "",
  topic: "",
  objectives: "",
  lesson_date: "",
  duration: 45,
  teaching_materials: [],
  teaching_aid: "",
  teaching_method: "",
  initiating_activities: "",
  developmental_activities: "",
  summary_conclusion: "",
  evaluation: "",
  assignment: "",
  week: "",
};

const LessonPlan = () => {
  const [formData, setFormData] = useState(initialFormData);

  const [subjects, setSubjects] = useState([]);
  const [gradeClasses, setGradeClasses] = useState([]);
  const [periods, setPeriods] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const [errors, setErrors] = useState({});
  const [serverMessage, setServerMessage] = useState("");

  const accessToken = localStorage.getItem("access");

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }),
    [accessToken]
  );

  useEffect(() => {
    let mounted = true;

    const loadFormData = async () => {
      setLoadingData(true);

      try {
        const [subjectsResponse, classesResponse, periodsResponse] =
          await Promise.all([
            api.get("/assigned-subjects/", authConfig),
            api.get("/assigned-grade-classes/", authConfig),
            api.get("/api/users/periods/", authConfig),
          ]);

        if (!mounted) return;

        setSubjects(
          Array.isArray(subjectsResponse.data)
            ? subjectsResponse.data
            : subjectsResponse.data?.results || []
        );

        setGradeClasses(
          Array.isArray(classesResponse.data)
            ? classesResponse.data
            : classesResponse.data?.results || []
        );

        setPeriods(
          Array.isArray(periodsResponse.data)
            ? periodsResponse.data
            : periodsResponse.data?.results || []
        );
      } catch (error) {
        console.error("Unable to load lesson-plan data:", error);

        if (mounted) {
          Swal.fire({
            icon: "error",
            title: "Unable to load form",
            text:
              getErrorMessage(error) ||
              "Subjects, classes, or periods could not be loaded.",
            confirmButtonColor: COLORS.black,
          });
        }
      } finally {
        if (mounted) {
          setLoadingData(false);
        }
      }
    };

    loadFormData();

    return () => {
      mounted = false;
    };
  }, [authConfig]);

  function getErrorMessage(error) {
    const data = error?.response?.data;

    if (!data) {
      return error?.message || "";
    }

    if (typeof data === "string") {
      return data;
    }

    if (data.detail) {
      return Array.isArray(data.detail)
        ? data.detail.join(" ")
        : String(data.detail);
    }

    if (data.non_field_errors) {
      return Array.isArray(data.non_field_errors)
        ? data.non_field_errors.join(" ")
        : String(data.non_field_errors);
    }

    return "";
  }

  const formatFieldError = (value) => {
    if (!value) return "";

    if (Array.isArray(value)) {
      return value
        .map((item) =>
          typeof item === "string"
            ? item
            : item?.message || JSON.stringify(item)
        )
        .join(" ");
    }

    if (typeof value === "object") {
      return Object.values(value)
        .flat()
        .map((item) =>
          typeof item === "string"
            ? item
            : item?.message || JSON.stringify(item)
        )
        .join(" ");
    }

    return String(value);
  };

  const parseServerErrors = (data) => {
    const parsed = {};

    if (!data || typeof data !== "object") {
      return parsed;
    }

    Object.entries(data).forEach(([key, value]) => {
      parsed[key] = formatFieldError(value);
    });

    return parsed;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [field]: "",
    }));

    setServerMessage("");
  };

  const handleMaterialChange = (material) => (event) => {
    setFormData((previous) => {
      const current = previous.teaching_materials || [];

      if (event.target.checked) {
        if (current.includes(material)) {
          return previous;
        }

        return {
          ...previous,
          teaching_materials: [...current, material],
        };
      }

      return {
        ...previous,
        teaching_materials: current.filter(
          (item) => item !== material
        ),
      };
    });

    setErrors((previous) => ({
      ...previous,
      teaching_materials: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.subject) {
      newErrors.subject = "Please select a subject.";
    }

    if (!formData.grade_class) {
      newErrors.grade_class = "Please select a grade class.";
    }

    if (!formData.period) {
      newErrors.period = "Please select a period.";
    }

    if (!formData.topic.trim()) {
      newErrors.topic = "Please enter the lesson topic.";
    }

    if (!formData.objectives.trim()) {
      newErrors.objectives =
        "Please provide the instructional objectives.";
    }

    if (!formData.lesson_date) {
      newErrors.lesson_date = "Please select the lesson date.";
    }

    if (
      formData.duration === "" ||
      Number(formData.duration) <= 0
    ) {
      newErrors.duration =
        "Lesson duration must be greater than zero.";
    }

    if (!formData.teaching_method.trim()) {
      newErrors.teaching_method =
        "Please describe the teaching method.";
    }

    if (!formData.initiating_activities.trim()) {
      newErrors.initiating_activities =
        "Please provide the initiating activities.";
    }

    if (!formData.developmental_activities.trim()) {
      newErrors.developmental_activities =
        "Please provide the main/developmental activities.";
    }

    if (!formData.summary_conclusion.trim()) {
      newErrors.summary_conclusion =
        "Please provide the lesson summary/conclusion.";
    }

    if (!formData.evaluation.trim()) {
      newErrors.evaluation =
        "Please provide the lesson evaluation.";
    }

    if (!formData.week.trim()) {
      newErrors.week = "Please enter the lesson week.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setServerMessage("");

    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Lesson Plan",
        text: "Please complete the required sections before submitting.",
        confirmButtonColor: COLORS.black,
      });

      return;
    }

    setLoading(true);

    const payload = {
      subject: Number(formData.subject),
      grade_class: Number(formData.grade_class),
      period: Number(formData.period),
      topic: formData.topic.trim(),
      objectives: formData.objectives.trim(),
      lesson_date: formData.lesson_date,
      duration: Number(formData.duration),
      teaching_materials: formData.teaching_materials,

      teaching_aid: formData.teaching_aid.trim(),

      teaching_method: formData.teaching_method.trim(),
      initiating_activities:
        formData.initiating_activities.trim(),
      developmental_activities:
        formData.developmental_activities.trim(),
      summary_conclusion:
        formData.summary_conclusion.trim(),
      evaluation: formData.evaluation.trim(),
      assignment: formData.assignment.trim(),
      week: formData.week.trim(),
    };

    try {
      const response = await api.post(
        "/lesson-plans/",
        payload,
        authConfig
      );

      const createdPlan = response.data;

      await Swal.fire({
        icon: "success",
        title: "Lesson Plan Submitted",
        html: `
          <div style="font-size:14px;line-height:1.7">
            <strong>Your lesson plan has been submitted successfully.</strong>
            <br />
            ${
              createdPlan?.topic
                ? `Topic: ${createdPlan.topic}`
                : ""
            }
          </div>
        `,
        confirmButtonColor: COLORS.black,
      });

      setFormData({
        ...initialFormData,
        lesson_date: getToday(),
      });

      setErrors({});
      setServerMessage("");
    } catch (error) {
      console.error("Lesson plan submission failed:", error);

      const data = error?.response?.data;
      const parsedErrors = parseServerErrors(data);

      setErrors(parsedErrors);

      const generalMessage =
        getErrorMessage(error) ||
        parsedErrors.non_field_errors ||
        "The lesson plan could not be submitted.";

      setServerMessage(generalMessage);

      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: generalMessage,
        confirmButtonColor: COLORS.black,
      });
    } finally {
      setLoading(false);
    }
  };

  const getToday = () => {
    const today = new Date();

    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const selectedMaterialCount =
    formData.teaching_materials.length;

  const sectionTitle = (
    number,
    icon,
    title,
    description
  ) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        mb: 2.5,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: "11px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: COLORS.black,
          color: COLORS.deepYellow,
          fontWeight: 800,
        }}
      >
        {number}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Stack
          direction="row"
          spacing={0.8}
          alignItems="center"
        >
          {icon}

          <Typography
            sx={{
              fontSize: {
                xs: "1rem",
                sm: "1.08rem",
              },
              fontWeight: 800,
              color: COLORS.text,
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Typography
          sx={{
            color: COLORS.muted,
            fontSize: "0.82rem",
            mt: 0.35,
            lineHeight: 1.5,
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: COLORS.white,
      transition: "all 0.2s ease",

      "& fieldset": {
        borderColor: COLORS.border,
      },

      "&:hover fieldset": {
        borderColor: COLORS.camel,
      },

      "&.Mui-focused fieldset": {
        borderColor: COLORS.deepYellow,
        borderWidth: "2px",
      },
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: COLORS.deepYellow,
    },

    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
  };

  const renderSelect = ({
    label,
    field,
    items,
    icon,
  }) => (
    <FormControl
      fullWidth
      required
      error={Boolean(errors[field])}
    >
      <InputLabel>{label}</InputLabel>

      <Select
        value={formData[field]}
        label={label}
        onChange={handleChange(field)}
        sx={{
          borderRadius: "10px",
          backgroundColor: COLORS.white,

          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: COLORS.border,
          },

          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: COLORS.camel,
          },

          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: COLORS.deepYellow,
            borderWidth: "2px",
          },

          "& .MuiSelect-icon": {
            color: COLORS.camel,
          },
        }}
        startAdornment={icon}
      >
        <MenuItem value="">
          <em>Select {label.toLowerCase()}</em>
        </MenuItem>

        {items.map((item) => (
          <MenuItem
            key={item.id}
            value={item.id}
          >
            {item.name}
          </MenuItem>
        ))}
      </Select>

      {errors[field] && (
        <FormHelperText>
          {errors[field]}
        </FormHelperText>
      )}
    </FormControl>
  );

  if (loadingData) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: COLORS.lightGray,
        }}
      >
        <Stack
          spacing={2}
          alignItems="center"
        >
          <CircularProgress
            size={38}
            sx={{
              color: COLORS.deepYellow,
            }}
          />

          <Typography
            sx={{
              color: COLORS.muted,
              fontWeight: 600,
            }}
          >
            Loading lesson plan workspace...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: COLORS.lightGray,
        py: {
          xs: 1.5,
          sm: 3,
          md: 4,
        },
        px: {
          xs: 1,
          sm: 2,
          md: 3,
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1250,
          mx: "auto",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: {
              xs: "12px",
              sm: "16px",
            },
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.white,
          }}
        >
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              background: `linear-gradient(135deg, ${COLORS.black} 0%, #242424 100%)`,
              px: {
                xs: 2,
                sm: 3,
                md: 4,
              },
              py: {
                xs: 2.5,
                sm: 3.5,
              },
              borderBottom: `5px solid ${COLORS.deepYellow}`,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: 170,
                height: 170,
                borderRadius: "50%",
                border: "1px solid rgba(212,160,23,0.18)",
                right: -65,
                top: -90,
              }}
            />

            <Box
              sx={{
                position: "absolute",
                width: 110,
                height: 110,
                borderRadius: "50%",
                border: "1px solid rgba(255,255,255,0.08)",
                right: 40,
                bottom: -75,
              }}
            />

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                justifyContent="space-between"
                alignItems={{
                  xs: "flex-start",
                  sm: "center",
                }}
                gap={2}
              >
                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 0.7 }}
                  >
                    <MenuBookOutlined
                      sx={{
                        color: COLORS.deepYellow,
                        fontSize: 25,
                      }}
                    />

                    <Typography
                      sx={{
                        color: COLORS.deepYellow,
                        fontSize: "0.76rem",
                        fontWeight: 800,
                        letterSpacing: "1.4px",
                        textTransform: "uppercase",
                      }}
                    >
                      Teacher Workspace
                    </Typography>
                  </Stack>

                  <Typography
                    sx={{
                      color: COLORS.white,
                      fontSize: {
                        xs: "1.45rem",
                        sm: "1.8rem",
                        md: "2rem",
                      },
                      fontWeight: 850,
                      lineHeight: 1.15,
                    }}
                  >
                    Create Lesson Plan
                  </Typography>

                  <Typography
                    sx={{
                      color: "#C9C9C9",
                      mt: 0.8,
                      maxWidth: 700,
                      fontSize: {
                        xs: "0.82rem",
                        sm: "0.9rem",
                      },
                      lineHeight: 1.6,
                    }}
                  >
                    Prepare a complete classroom lesson plan with
                    objectives, activities, teaching materials,
                    teaching aids, evaluation, and assignment details.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: {
                      xs: "none",
                      sm: "flex",
                    },
                    alignItems: "center",
                    justifyContent: "center",
                    width: 66,
                    height: 66,
                    borderRadius: "16px",
                    backgroundColor:
                      "rgba(212,160,23,0.12)",
                    border:
                      "1px solid rgba(212,160,23,0.35)",
                  }}
                >
                  <SchoolOutlined
                    sx={{
                      fontSize: 34,
                      color: COLORS.deepYellow,
                    }}
                  />
                </Box>
              </Stack>
            </Box>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              p: {
                xs: 1.75,
                sm: 3,
                md: 4,
              },
            }}
          >
            {serverMessage && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: "10px",
                }}
              >
                {serverMessage}
              </Alert>
            )}

            <Box sx={{ mb: 4 }}>
              {sectionTitle(
                "01",
                <ClassOutlined
                  sx={{
                    fontSize: 20,
                    color: COLORS.deepYellow,
                  }}
                />,
                "Lesson Information",
                "Select the academic information associated with this lesson."
              )}

              <Grid container spacing={2.2}>
                <Grid item xs={12} md={4}>
                  {renderSelect({
                    label: "Subject",
                    field: "subject",
                    items: subjects,
                    icon: null,
                  })}
                </Grid>

                <Grid item xs={12} md={4}>
                  {renderSelect({
                    label: "Grade Class",
                    field: "grade_class",
                    items: gradeClasses,
                    icon: null,
                  })}
                </Grid>

                <Grid item xs={12} md={4}>
                  {renderSelect({
                    label: "Period",
                    field: "period",
                    items: periods,
                    icon: null,
                  })}
                </Grid>

                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    required
                    label="Lesson Topic"
                    value={formData.topic}
                    onChange={handleChange("topic")}
                    error={Boolean(errors.topic)}
                    helperText={errors.topic}
                    placeholder="Enter the specific topic being taught"
                    InputProps={{
                      startAdornment: (
                        <TopicOutlined
                          sx={{
                            mr: 1,
                            color: COLORS.camel,
                          }}
                        />
                      ),
                    }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2.5}>
                  <TextField
                    fullWidth
                    required
                    type="date"
                    label="Lesson Date"
                    value={formData.lesson_date}
                    onChange={handleChange("lesson_date")}
                    error={Boolean(errors.lesson_date)}
                    helperText={errors.lesson_date}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      startAdornment: (
                        <CalendarMonthOutlined
                          sx={{
                            mr: 1,
                            color: COLORS.camel,
                          }}
                        />
                      ),
                    }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2.5}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Duration (Minutes)"
                    value={formData.duration}
                    onChange={handleChange("duration")}
                    error={Boolean(errors.duration)}
                    helperText={
                      errors.duration || "Default: 45 minutes"
                    }
                    inputProps={{
                      min: 1,
                      max: 600,
                    }}
                    InputProps={{
                      startAdornment: (
                        <TimerOutlined
                          sx={{
                            mr: 1,
                            color: COLORS.camel,
                          }}
                        />
                      ),
                    }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="Week"
                    value={formData.week}
                    onChange={handleChange("week")}
                    error={Boolean(errors.week)}
                    helperText={
                      errors.week ||
                      "Example: Week 1, Week 2, Week 3"
                    }
                    placeholder="e.g. Week 1"
                    InputProps={{
                      startAdornment: (
                        <EventNoteOutlined
                          sx={{
                            mr: 1,
                            color: COLORS.camel,
                          }}
                        />
                      ),
                    }}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Box sx={{ mb: 4 }}>
              {sectionTitle(
                "02",
                <DescriptionOutlined
                  sx={{
                    fontSize: 20,
                    color: COLORS.deepYellow,
                  }}
                />,
                "Learning Objectives",
                "State what learners should know, understand, or demonstrate by the end of the lesson."
              )}

              <TextField
                fullWidth
                required
                multiline
                minRows={4}
                label="Instructional Objectives"
                value={formData.objectives}
                onChange={handleChange("objectives")}
                error={Boolean(errors.objectives)}
                helperText={
                  errors.objectives ||
                  "Use clear and measurable learning outcomes."
                }
                placeholder={
                  "Example:\n1. Define the key concept.\n2. Identify important examples.\n3. Demonstrate understanding through an activity."
                }
                sx={inputSx}
              />
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Box sx={{ mb: 4 }}>
              {sectionTitle(
                "03",
                <ScienceOutlined
                  sx={{
                    fontSize: 20,
                    color: COLORS.deepYellow,
                  }}
                />,
                "Teaching Approach",
                "Describe how the lesson will be introduced and delivered."
              )}

              <Grid container spacing={2.2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={3}
                    label="Teaching Method"
                    value={formData.teaching_method}
                    onChange={handleChange("teaching_method")}
                    error={Boolean(errors.teaching_method)}
                    helperText={errors.teaching_method}
                    placeholder="Describe the instructional methods, strategies, or approaches you will use."
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={4}
                    label="Initiating Activities / Introduction"
                    value={formData.initiating_activities}
                    onChange={handleChange(
                      "initiating_activities"
                    )}
                    error={Boolean(
                      errors.initiating_activities
                    )}
                    helperText={
                      errors.initiating_activities ||
                      "Explain how you will introduce and engage learners."
                    }
                    placeholder="Opening activity, review, questions, demonstration, motivation, or prior-knowledge connection..."
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={5}
                    label="Developmental Associating Activities / Main Activities"
                    value={formData.developmental_activities}
                    onChange={handleChange(
                      "developmental_activities"
                    )}
                    error={Boolean(
                      errors.developmental_activities
                    )}
                    helperText={
                      errors.developmental_activities ||
                      "Describe the main teaching and learner activities."
                    }
                    placeholder="Teacher activities, learner activities, demonstrations, guided practice, group work, questions, exercises..."
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={4}
                    label="Summary / Conclusion"
                    value={formData.summary_conclusion}
                    onChange={handleChange(
                      "summary_conclusion"
                    )}
                    error={Boolean(errors.summary_conclusion)}
                    helperText={
                      errors.summary_conclusion ||
                      "Explain how the lesson will be summarized and concluded."
                    }
                    placeholder="Summarize the key learning points, clarify misconceptions, and close the lesson..."
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Box sx={{ mb: 4 }}>
              {sectionTitle(
                "04",
                <AutoStoriesOutlined
                  sx={{
                    fontSize: 20,
                    color: COLORS.deepYellow,
                  }}
                />,
                "Teaching Materials",
                "Select the materials and resources you intend to use during the lesson."
              )}

              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: "12px",
                  p: {
                    xs: 1.5,
                    sm: 2,
                  },
                  backgroundColor: "#FCFCFC",
                }}
              >
                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  justifyContent="space-between"
                  alignItems={{
                    xs: "flex-start",
                    sm: "center",
                  }}
                  gap={1}
                  sx={{ mb: 1 }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: COLORS.text,
                    }}
                  >
                    Available Materials
                  </Typography>

                  <Box
                    sx={{
                      px: 1.2,
                      py: 0.5,
                      borderRadius: "20px",
                      backgroundColor: COLORS.lightCamel,
                      color: COLORS.black,
                      fontSize: "0.75rem",
                      fontWeight: 800,
                    }}
                  >
                    {selectedMaterialCount} selected
                  </Box>
                </Stack>

                <FormGroup>
                  <Grid container>
                    {MATERIAL_OPTIONS.map((material) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        key={material.value}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.teaching_materials.includes(
                                material.value
                              )}
                              onChange={handleMaterialChange(
                                material.value
                              )}
                              sx={{
                                color: COLORS.camel,
                                "&.Mui-checked": {
                                  color: COLORS.deepYellow,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{
                                fontSize: "0.84rem",
                                color: COLORS.text,
                              }}
                            >
                              {material.label}
                            </Typography>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>

                {errors.teaching_materials && (
                  <FormHelperText error>
                    {errors.teaching_materials}
                  </FormHelperText>
                )}
              </Paper>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Section 5 - Teaching Aid */}
            <Box sx={{ mb: 4 }}>
              {sectionTitle(
                "05",
                <AutoStoriesOutlined
                  sx={{
                    fontSize: 20,
                    color: COLORS.deepYellow,
                  }}
                />,
                "Teaching Aid",
                "Describe the teaching aids you will use to make the lesson clearer, practical, and engaging."
              )}

              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Teaching Aid"
                value={formData.teaching_aid}
                onChange={handleChange("teaching_aid")}
                error={Boolean(errors.teaching_aid)}
                helperText={
                  errors.teaching_aid ||
                  "Examples: real objects, pictures, charts, models, maps, flash cards, demonstrations, videos, or locally available materials."
                }
                placeholder={
                  "Describe the teaching aids you will use during the lesson..."
                }
                sx={inputSx}
              />
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Section 6 */}
            <Box sx={{ mb: 4 }}>
              {sectionTitle(
                "06",
                <CheckCircleOutline
                  sx={{
                    fontSize: 20,
                    color: COLORS.deepYellow,
                  }}
                />,
                "Evaluation & Assignment",
                "Record how learning will be checked and what learners should complete after the lesson."
              )}

              <Grid container spacing={2.2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={4}
                    label="Evaluation"
                    value={formData.evaluation}
                    onChange={handleChange("evaluation")}
                    error={Boolean(errors.evaluation)}
                    helperText={
                      errors.evaluation ||
                      "Describe questions, exercises, observation, tests, oral responses, or other assessment activities."
                    }
                    placeholder="Explain how you will determine whether learners achieved the objectives..."
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Assignment"
                    value={formData.assignment}
                    onChange={handleChange("assignment")}
                    error={Boolean(errors.assignment)}
                    helperText={
                      errors.assignment ||
                      "Optional. Enter homework, practice, research, or follow-up work."
                    }
                    placeholder="Enter any homework, practice exercise, research, or follow-up activity..."
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </Box>

            <Paper
              elevation={0}
              sx={{
                mb: 3,
                p: {
                  xs: 1.5,
                  sm: 2,
                },
                borderRadius: "12px",
                border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.lightGray,
              }}
            >
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={2}
                alignItems={{
                  xs: "flex-start",
                  sm: "center",
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "10px",
                    backgroundColor: COLORS.black,
                  }}
                >
                  <AssignmentOutlined
                    sx={{
                      color: COLORS.deepYellow,
                    }}
                  />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.9rem",
                      color: COLORS.text,
                    }}
                  >
                    Ready to submit?
                  </Typography>

                  <Typography
                    sx={{
                      color: COLORS.muted,
                      fontSize: "0.8rem",
                      lineHeight: 1.5,
                    }}
                  >
                    Review the lesson information and instructional
                    sections before submitting. The teacher account
                    is automatically attached by the backend.
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress
                    size={20}
                    color="inherit"
                  />
                ) : (
                  <CheckCircleOutline />
                )
              }
              sx={{
                height: 54,
                borderRadius: "11px",
                backgroundColor: COLORS.black,
                color: COLORS.white,
                fontSize: {
                  xs: "0.92rem",
                  sm: "1rem",
                },
                fontWeight: 800,
                textTransform: "none",
                boxShadow:
                  "0 6px 18px rgba(0,0,0,0.14)",
                transition: "all 0.25s ease",

                "&:hover": {
                  backgroundColor: COLORS.deepYellow,
                  color: COLORS.black,
                  boxShadow:
                    "0 8px 22px rgba(212,160,23,0.28)",
                },

                "&:disabled": {
                  backgroundColor: "#858585",
                  color: COLORS.white,
                },
              }}
            >
              {loading
                ? "Submitting Lesson Plan..."
                : "Submit Lesson Plan"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default LessonPlan;